import { useState, useEffect } from "react";

const SECTION_IDS = [
  "hero",
  "problem",
  "how-it-works",
  "platform",
  "outcomes",
  "faq",
];

/**
 * Custom hook that tracks which section is currently in view.
 *
 * Uses IntersectionObserver to detect which section occupies
 * the most viewport space. Returns the active section ID.
 *
 * @param threshold - How much of a section must be visible (0-1). Default 0.3.
 * @returns The ID of the currently active section, or null if none.
 */
export function useScrollSpy(threshold = 0.3): string | null {
  const [activeSection, setActiveSection] = useState<string | null>(null);

  useEffect(() => {
    const elements = SECTION_IDS.map((id) =>
      document.getElementById(id),
    ).filter(Boolean) as HTMLElement[];

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the entry with the largest intersection ratio
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        const first = visible[0];
        if (first) {
          setActiveSection(first.target.id);
        }
      },
      {
        rootMargin: "-80px 0px 0px 0px", // Account for fixed nav height
        threshold: [0, threshold, 0.5, 1],
      },
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [threshold]);

  return activeSection;
}

/**
 * Returns the appropriate scroll behavior respecting prefers-reduced-motion.
 */
export function getScrollBehavior(): ScrollBehavior {
  if (typeof window === "undefined") return "auto";
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ? "auto"
    : "smooth";
}
